import Permissions from '@app/src/auth/permissions'
import { usePermissions } from '@app/src/auth/permissions/usePermissions'
import { CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS } from '@app/src/components/CreateNewRequestModal/Common/SelectFilterStep'
import useQueryFilters, { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import type { QueryParamType } from '@app/src/hooks/queryState'
import useActorTypes from '@app/src/hooks/useActorTypes'
import { AUTOMATION_QUESTION_KEYS } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreateAutomationModal'
import ColleagueOwnerFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/ColleagueOwnerFilterSelect'
import CountryFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/CountryFilterSelect'
import CustomCategoriesFilterGrid, {
  useCustomCategories,
} from '@app/src/pages/Configurations/ConfigurationsPages/Common/CustomCategoriesFilterGrid'
import FinalRiskRatingFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/FinalRiskRatingFilterSelect'
import ProviderCategoryFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/ProviderCategoryFilterSelect'
import { mapToQueryParamType, normalizeFilterGroups } from '@app/src/pages/QuestionEditor/AnswerClassification/utils'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import FilterSelect from '@app/src/pages/ResourceCollection/Filters/FilterSelect'
import OptionWithLabel from '@app/src/pages/ResourceCollection/Filters/OptionWithLabel'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { ProviderStandardCategoryNames } from '@app/src/types/categories'
import { MAPPING_NODE_TIER_OPTIONS } from '@app/src/utils/providerCategoryOptions'
import { Chip, Grid, Typography } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { ProductStatus } from '@app/src/types/product'
import { OptionIcon } from '@app/src/components/Form/Select'
import { groupOptionsContiguously } from '@app/src/utils/groupOptionsContiguously'

interface AutomationCategoriesProps {
  hideProductCategories?: boolean
}

const AutomationCategories: React.FC<AutomationCategoriesProps> = ({ hideProductCategories }) => {
  const { formatMessage } = useIntl()
  const { renderWithPermission } = usePermissions()
  const { productMapping } = useFlags()
  const { items: actorTypes } = useActorTypes()
  const { categories } = useCustomCategories()
  const { setValue, getValues } = useFormContext()
  const filters = useGetApiQueryFilters(CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS)

  const { setQueryFilters } = useQueryFilters(CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS)

  // Initialize URL query filters from form values when editing an existing automation
  useEffect(() => {
    const initialFilterValues = getValues(AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS)
    if (!initialFilterValues) return

    const normalized = normalizeFilterGroups(initialFilterValues)
    const mappedFilters = normalized.map(mapToQueryParamType).filter((f): f is QueryParamType => f !== undefined)

    setQueryFilters(mappedFilters)
    setValue(AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS, normalized)
  }, [])

  const productStatusOptions = useMemo(
    () => [
      {
        label: formatMessage({ id: 'schemas.product.productStatusValues.NotSelected' }),
        value: ProductStatus.NotSelected,
      },
      { label: formatMessage({ id: 'schemas.product.productStatusValues.Active' }), value: ProductStatus.Active },
      { label: formatMessage({ id: 'schemas.product.productStatusValues.Inactive' }), value: ProductStatus.Inactive },
    ],
    [formatMessage],
  )

  useDeepCompareEffect(() => {
    setValue(AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS, filters)
  }, [filters])

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle1">
          {formatMessage({ id: 'form.createAutomation.automationType.generalCategories' })}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <CountryFilterSelect />
      </Grid>
      <Grid item xs={6}>
        <ColleagueOwnerFilterSelect />
      </Grid>
      <Grid item xs={6}>
        <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.ActivityStatus} />
      </Grid>
      <Grid item xs={6}>
        <FinalRiskRatingFilterSelect />
      </Grid>
      <Grid item xs={6}>
        <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.Priority} />
      </Grid>
      <Grid item xs={6}>
        <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.ApprovalStatus} />
      </Grid>
      {renderWithPermission({
        [Permissions.SOURCING_USER]: (
          <>
            <Grid item xs={6}>
              <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.SupplierUsage} />
            </Grid>
            <Grid item xs={6}>
              <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.Tier} />
            </Grid>
          </>
        ),
      })}
      {Boolean(categories?.length) && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {formatMessage({ id: 'form.createAutomation.automationType.customCategories' })}
            </Typography>
          </Grid>
          <CustomCategoriesFilterGrid
            formKey={AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS}
            categories={categories ?? []}
          />
        </>
      )}
      {productMapping && !hideProductCategories && (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {formatMessage({ id: 'form.createAutomation.automationType.productMapping' })}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Filter operator={Operators.In} name="mappingNodes.actorType.id">
              {({ value, onChange }) => (
                <FilterSelect
                  size="small"
                  multiple
                  groupBy={option => option.additionalText ?? ''}
                  renderOption={(props, option, { selected }) => (
                    <OptionWithLabel
                      key={Number(option.value)}
                      props={props}
                      label={option?.label}
                      multiple
                      selected={selected}
                    />
                  )}
                  options={groupOptionsContiguously(
                    actorTypes.map(actorType => ({
                      label: formatMessage({ id: `schemas.mappingNode.actorTypeValues.${actorType.name}` }),
                      value: actorType.id,
                      additionalText: actorType.actorTypeGroup
                        ? formatMessage({ id: `schemas.mappingNode.actorTypeGroups.${actorType.actorTypeGroup}` })
                        : formatMessage({ id: `schemas.product.productIndustry.${actorType.industry}` }),
                    })),
                    option => option.additionalText,
                  )}
                  renderTags={(value, getTagProps) =>
                    value?.map(
                      (option, index) =>
                        option && (
                          <Chip
                            {...getTagProps({ index })}
                            key={option?.value?.toString() || index}
                            label={option?.label}
                            size="small"
                          />
                        ),
                    )
                  }
                  onChange={value => onChange(value)}
                  fieldLabel={formatMessage({ id: 'schemas.mappingNode.actorType' })}
                  value={[value].flat()}
                />
              )}
            </Filter>
          </Grid>
          <Grid item xs={6}>
            <Filter operator={Operators.In} name="mappingNodes.tier">
              {({ value, onChange }) => (
                <FilterSelect
                  size="small"
                  multiple
                  options={
                    MAPPING_NODE_TIER_OPTIONS?.map(tier => ({
                      label: tier.toString(),
                      value: tier.toString(),
                    })) ?? []
                  }
                  onChange={value => onChange(value)}
                  fieldLabel={formatMessage({ id: 'schemas.mappingNode.tier' })}
                  value={[value].flat()}
                />
              )}
            </Filter>
          </Grid>
          <Grid item xs={6}>
            <Filter operator={Operators.In} name="mappingNodes.product.productStatus">
              {({ value, onChange }) => (
                <FilterSelect
                  size="small"
                  multiple
                  groupBy={option => option.additionalText ?? ''}
                  options={productStatusOptions}
                  renderOption={(props, option, { selected }): JSX.Element => {
                    return (
                      <li {...props} key={option.value?.toString() ?? option.label}>
                        <OptionIcon selected={selected} multiple />
                        {option.label}
                      </li>
                    )
                  }}
                  renderTags={(value, getTagProps) =>
                    value?.map(
                      (option, index) =>
                        option && (
                          <Chip
                            {...getTagProps({ index })}
                            key={option?.value?.toString() || index}
                            label={option.label}
                            size="small"
                          />
                        ),
                    )
                  }
                  onChange={value => onChange(value)}
                  fieldLabel={formatMessage({ id: 'schemas.product.productStatus' })}
                  value={[value].flat()}
                />
              )}
            </Filter>
          </Grid>
        </>
      )}
    </Grid>
  )
}
export default AutomationCategories
